import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import agent from '../api/axios';

interface Subscription {
  id: string;
  _id?: string;
  Id?: string;
  name: string;
  price: number;
  term: string;
  active: boolean;
  features?: string[];
}

// Features included in all plans
const planFeatures = [
  'Unlimited contractors',
  'Time tracking & timesheets',
  'Automatic pay calculations',
  '1099-MISC generation',
  'Payment history & reports',
  'Data export (CSV)',
  'Email support',
];

export default function PricingPage() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        const data = await agent.Subscriptions.get();
        const active = (data ?? []).filter((s: Subscription) => s.active);
        setSubscriptions(active);
      } catch (err) {
        console.error('Failed to load subscriptions', err);
        setError('Unable to load pricing. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptions();
  }, []);

  const getSubscriptionId = (sub: Subscription) => sub.id ?? sub._id ?? sub.Id;

  const getFeatures = (sub: Subscription) => {
    if (sub.features && sub.features.length > 0) {
      return sub.features;
    }
    return planFeatures;
  };

  const isPopular = (index: number) => index === 1; // Mark second plan as popular

  const isYearly = (sub: Subscription) =>
    sub.term.toLowerCase().includes('year');

  const getYearlySavings = (sub: Subscription): number | null => {
    if (!isYearly(sub)) return null;
    const monthlyPlan = subscriptions.find(
      (s) => s.term.toLowerCase().includes('month')
    );
    if (!monthlyPlan) return null;
    const yearlyIfMonthly = monthlyPlan.price * 12;
    const savings = yearlyIfMonthly - sub.price;
    return savings > 0 ? savings : null;
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          pt: 12,
          pb: 8,
          background: 'linear-gradient(135deg, #f8faf8 0%, #e8f5e9 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Chip label="Pricing" sx={{ mb: 2 }} color="primary" />
            <Typography variant="h2" sx={{ fontWeight: 700, mb: 2 }}>
              Simple, Transparent Pricing
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ lineHeight: 1.7 }}
            >
              Choose the plan that fits your business. No hidden fees, no
              surprises. Same features, choose your billing cycle.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Pricing Cards */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={48} />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography color="error" variant="h6">
                {error}
              </Typography>
              <Button
                variant="outlined"
                sx={{ mt: 2 }}
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            </Box>
          ) : subscriptions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography color="text.secondary" variant="h6">
                No pricing plans available at this time.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={4} justifyContent="center">
              {subscriptions.map((sub, index) => (
                <Grid item xs={12} sm={6} md={4} key={getSubscriptionId(sub)}>
                  <Card
                    sx={{
                      height: '100%',
                      position: 'relative',
                      overflow: 'visible',
                      border: isPopular(index) ? '2px solid' : '1px solid',
                      borderColor: isPopular(index)
                        ? 'primary.main'
                        : 'divider',
                      transform: isPopular(index) ? 'scale(1.02)' : 'none',
                      boxShadow: isPopular(index)
                        ? '0 12px 40px rgba(76, 175, 80, 0.2)'
                        : undefined,
                    }}
                  >
                    {isPopular(index) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -12,
                          left: '50%',
                          transform: 'translateX(-50%)',
                        }}
                      >
                        <Chip
                          icon={<StarIcon sx={{ fontSize: 16 }} />}
                          label="Most Popular"
                          color="primary"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    )}
                    <CardContent sx={{ p: 4, pt: isPopular(index) ? 5 : 4 }}>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 600, mb: 1, textAlign: 'center' }}
                      >
                        {sub.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: 'center', mb: 3 }}
                      >
                        {sub.term}
                      </Typography>
                      <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography
                          variant="h2"
                          component="span"
                          sx={{ fontWeight: 800, color: 'primary.main' }}
                        >
                          ${Number(sub.price ?? 0).toFixed(0)}
                        </Typography>
                        <Typography
                          variant="h6"
                          component="span"
                          color="text.secondary"
                        >
                          /
                          {sub.term.toLowerCase().includes('year')
                            ? 'year'
                            : 'month'}
                        </Typography>
                        {getYearlySavings(sub) && (
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 1,
                              color: 'success.main',
                              fontWeight: 600,
                            }}
                          >
                            Save ${getYearlySavings(sub)?.toFixed(0)}/year
                          </Typography>
                        )}
                      </Box>
                      <Button
                        variant={isPopular(index) ? 'contained' : 'outlined'}
                        fullWidth
                        size="large"
                        onClick={() => {
                          window.location.href = `${
                            process.env.REACT_APP_APP_URL || 'https://localhost:6001'
                          }/register?plan=${getSubscriptionId(sub)}`;
                        }}
                        sx={{ mb: 4 }}
                      >
                        Get Started
                      </Button>
                      <List dense>
                        {getFeatures(sub).map(
                          (feature, featureIndex) => (
                            <ListItem key={featureIndex} sx={{ px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckCircleIcon
                                  sx={{ color: 'primary.main', fontSize: 20 }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={feature}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          )
                        )}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 10, bgcolor: '#f8faf8' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
              Frequently Asked Questions
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {[
              {
                q: 'Can I cancel anytime?',
                a: 'Yes, you can cancel your subscription at any time. Cancellations take effect at the end of your current billing period, so you will retain access until the date you are paid through. No refunds are provided for partial periods.',
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit cards through our secure payment processor, Stripe.',
              },
              {
                q: 'Is my data secure?',
                a: 'Yes. All sensitive data including TINs and SSNs are encrypted at rest. We use industry-standard security practices.',
              },
              {
                q: 'Can I switch plans later?',
                a: 'Yes! You can switch between Monthly and Yearly plans at any time from your account settings.',
              },
              {
                q: 'What about refunds?',
                a: 'All sales are final. When you cancel, your subscription remains active until the end of your current billing period. No partial refunds are provided.',
              },
              {
                q: 'Do I get a refund if I cancel early?',
                a: 'No, we do not offer refunds for unused time. When you cancel, you will continue to have access until your current billing period ends.',
              },
            ].map((faq, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {faq.q}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {faq.a}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: 10,
          background: 'linear-gradient(135deg, #1E23FE 0%, #0D9E6F 100%)',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            sx={{ color: 'white', fontWeight: 700, mb: 2 }}
          >
            Ready to Get Started?
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: 'rgba(255,255,255,0.85)', mb: 4 }}
          >
            Get started today and start tracking contractor payments.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              window.location.href = `${
                process.env.REACT_APP_APP_URL || 'https://localhost:6001'
              }/register`;
            }}
            sx={{
              bgcolor: 'white',
              color: '#1E23FE',
              px: 6,
              py: 2,
              fontSize: '1.125rem',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.9)',
              },
            }}
          >
            Get Started Now
          </Button>
        </Container>
      </Box>
    </Box>
  );
}
